import {getSessionContext} from '../contexts/SessionResolver';
import MediaDeviceInfoWithState from './MediaDeviceInfoPlus';

/**
 * Shared "mute-while-disconnected" output policy.
 *
 * When a selected output device is unplugged we keep it pinned in the UI as
 * inactive (`isActive === false`) instead of auto-remapping to another speaker.
 * Live TTS for that language must then be muted, otherwise Chrome/Windows falls
 * back to the OS default device and languages leak across speakers.
 *
 * Both Dashboard (Controler + updatePanelConfiguration) and Two-Way
 * (updateBiPanel) need the exact same rule, so it lives here in one place.
 * The user's *saved* gain is never touched by these helpers — muting is a
 * transient live-playback concern, not a stored preference.
 */

/**
 * A selected output is active unless it is an inactive (disconnected)
 * `MediaDeviceInfoWithState`. Native `MediaDeviceInfo` objects do not carry the
 * custom `isActive` flag and are treated as active.
 */
export function isOutputDeviceActive(device: MediaDeviceInfo | null | undefined): boolean {
    return !!device && (device as MediaDeviceInfoWithState).isActive !== false;
}

/**
 * Live-playback gain for an output: the saved gain when the device is available,
 * or 0 (muted) while it is disconnected. Never persist the returned value.
 */
export function effectiveOutputGain(
    device: MediaDeviceInfo | null | undefined,
    savedGain: number,
): number {
    return isOutputDeviceActive(device) ? savedGain : 0;
}

/**
 * Apply the effective live volume for a translation output. Muting is derived
 * from the current device state; the saved gain is passed through untouched.
 * No-ops safely when no session/processor exists yet.
 */
export function applyLiveOutputVolume(
    translationKey: string,
    device: MediaDeviceInfo | null | undefined,
    savedGain: number,
): void {
    try {
        const gain = effectiveOutputGain(device, savedGain);
        getSessionContext()?.getText2Speech(translationKey)?.getAudioPlayer()?.setVolume(gain);
    } catch {
        // Session or TTS processor not ready — nothing to apply.
    }
}
